import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { noop } from 'lodash';
import { merge, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { Organisation, Team } from '@core/api';
import { APP_CONFIG, AppConfig } from '@core/config';
import { SharedFormsModule } from '@shared/forms';

import { TeamChipEditableComponent } from '../team-chip-editable/team-chip-editable.component';

type OnChangeFunction = (value: string[]) => void;
type OnTouchFunction = () => void;

@Component({
  selector: 'la-edit-team-tags',
  templateUrl: './team-tags.component.html',
  styleUrls: ['./team-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedFormsModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatMenuModule,
    TeamChipEditableComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditTeamTagsComponent),
      multi: true,
    },
  ],
})
export class EditTeamTagsComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() teams: Team[] = [];
  @Input() organization!: Organisation;
  @Input() required = false;

  private isTouched = false;

  errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (): boolean => this.required && this.isTouched && this.selectedElements.length === 0,
  };

  @Output() addTeam = new EventEmitter<Team>();
  @Output() removeTeam = new EventEmitter<Team>();

  @ViewChild('elementsInput') elementsInput!: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild(MatChipGrid) private chipGrid?: MatChipGrid;

  selectedElements: Team[] = [];
  autocompleteElements$!: Observable<Team[]>;
  visible = true;
  addOnBlur = false;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  inputControl = new FormControl();

  onChange: OnChangeFunction = noop;
  onTouched: OnTouchFunction = noop;

  private reset$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  config = inject<AppConfig>(APP_CONFIG);

  ngOnInit() {
    this.autocompleteElements$ = merge(this.inputControl.valueChanges, this.reset$).pipe(
      startWith(''),
      takeUntil(this.destroy$),
      map((value) => this.filterAutocompleteElements(value))
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.reset$.complete();
  }

  writeValue(ids: string[]): void {
    this.selectedElements = ids
      .map((id) => this.teams.find((element) => element.id === id))
      .filter((element): element is Team => element !== undefined);
    this.reset$.next();
  }

  registerOnChange(fn: OnChangeFunction): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchFunction): void {
    this.onTouched = fn;
  }

  add(event: MatChipInputEvent): void {
    const elementToAdd = this.teams.find((element) => element.name.toLowerCase() === event.value);

    if (elementToAdd) {
      this.selectedElements.push(elementToAdd);
      this.elementsChanged();
      this.addTeam.emit(elementToAdd);
    }

    this.elementsInput.nativeElement.value = '';
    this.inputControl.setValue(null);
  }

  remove(element: Team): void {
    const index = this.selectedElements.indexOf(element);

    if (index >= 0) {
      this.selectedElements.splice(index, 1);
      this.elementsChanged();
      this.removeTeam.emit(element);
    }

    this.inputControl.setValue(null);
    this.markTouched();
  }

  onInputBlur(): void {
    this.markTouched();
  }

  select(event: MatAutocompleteSelectedEvent): void {
    const elementToAdd = this.teams.find((element) => element.id === event.option.value.id);

    if (elementToAdd) {
      this.selectedElements.push(elementToAdd);
      this.elementsChanged();
      this.addTeam.emit(elementToAdd);
    }

    this.elementsInput.nativeElement.value = '';
    this.inputControl.setValue(null);
    this.chipGrid?.updateErrorState();
  }

  get canAddTeams() {
    return this.selectedElements.length < this.config.limits.teamTags;
  }

  private filterAutocompleteElements(value: string): Team[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';

    if (this.canAddTeams) {
      return this.teams
        .filter((element) => element.name.toLowerCase().indexOf(filterValue) === 0)
        .filter((element) => element.type !== 'ADMINISTRATION')
        .filter((elementToFilter) => !this.selectedElements.find((element) => element.id === elementToFilter.id));
    } else {
      return [];
    }
  }

  private markTouched(): void {
    if (!this.isTouched) {
      this.isTouched = true;
      this.onTouched();
    }
    this.chipGrid?.updateErrorState();
  }

  private elementsChanged(): void {
    this.onChange(this.selectedElements.map((element) => element.id));
  }
}
